import React, { useContext } from 'react';
import gettext from '@eb/gettext';
import { CoreEnv } from '@eb/context-gen';

import { DiscoveryEvent } from '../types';

import { UserContext, EnvContext, FacebookOptions } from '../context';

import { SmartEventCard } from './SmartEventCard';

const getLocationInfo = (venueName: string, venueAddress: string) => {
    //If we have both name and address add comma separated address
    if (venueAddress && venueName) {
        //ex. Eventbrite • San Francisco CA
        return `${venueName} \u2022 ${venueAddress}`;
    }

    return venueName || venueAddress;
};

const transformEventsForDisplay = (events: DiscoveryEvent[]) => {
    return events.map((ebEvent: DiscoveryEvent) => {
        let [startDate, startTime] = ebEvent.start.local.split('T');
        let subtitle;

        if (ebEvent.is_series_parent) {
            startDate = '';
            subtitle = gettext('MULTIPLE DATES');
        }

        return {
            subtitle,
            id: ebEvent.id,
            isFree: ebEvent.is_free,
            imageUrl: ebEvent?.logo?.url,
            name: ebEvent?.name.text,
            isOnlineEvent: ebEvent.online_event,
            formattedPriceString: ebEvent.is_free ? '' : ebEvent.price_range,
            startTime: startTime,
            startDate: ebEvent.hide_start_date ? undefined : startDate,
            timezome: ebEvent.start.timezone,
            url: ebEvent.url,
            locationInfo: getLocationInfo(
                ebEvent?.venue?.name,
                ebEvent?.venue?.address?.localized_area_display,
            ),
            savedByYou: ebEvent.bookmarked,
            eventCollections: ebEvent?.collections,
        };
    });
};

const getShareOptionsForEvent = (
    env: CoreEnv,
    facebookOptions: FacebookOptions,
) => {
    const {
        /* ShareOptions data */
        isMobile,
        serverUrl,
        localeInfo: {
            twitter_handle: twitterHandle,
            facebook_locale: facebookLocale,
            facebook_page: facebookPage,
        },
    } = env;

    return {
        isMobile,
        serverUrl,
        twitterHandle,
        facebookOptions: {
            ...facebookOptions,
            locale: facebookLocale,
            page: facebookPage,
        },
    };
};

interface EventRendererProps {
    events: DiscoveryEvent[];
    removeSaveHandlers?: boolean;
}

export interface EventCardEventData {
    id: string;
    isFree: boolean;
    imageUrl: string;
    name: string;
    isOnlineEvent: boolean;
    startTime: string;
    timezome: string;
    url: string;
    locationInfo: string;
    savedByYou: boolean;
    startDate?: string;
    subtitle?: string;
    formattedPriceString?: string;
    eventCollections?: string[];
}

export const EventRenderer: React.FunctionComponent<EventRendererProps> = (
    props,
) => {
    const { env, facebookOptions } = useContext(EnvContext);
    const user = useContext(UserContext);
    const isAuthenticated = user?.isAuthenticated;
    const userCollections = user?.collections?.userCollections;

    if (props.events.length === 0) {
        return (
            <div className="eds-text-bl">
                {gettext('Sorry, there are no upcoming events')}
            </div>
        );
    }

    const transformedEventData = transformEventsForDisplay(props.events);
    const shareOptions = getShareOptionsForEvent(
        env as CoreEnv,
        facebookOptions as FacebookOptions,
    );

    return (
        <div>
            {transformedEventData.map((eventData: EventCardEventData) => (
                <SmartEventCard
                    hideSaveButton={!!props.removeSaveHandlers}
                    key={eventData.id}
                    style="standard"
                    type="list"
                    imageStyle="fixed"
                    containerSpacingClasses="eds-l-pad-all-2"
                    isConsumer={true}
                    ticketsBy="Eventbrite"
                    shouldConvertToUserTimezone={true}
                    isAuthenticated={isAuthenticated}
                    shareOptions={{ ...shareOptions, eventUrl: eventData.url }}
                    userCollections={userCollections}
                    {...eventData}
                />
            ))}
        </div>
    );
};
